import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { themes, type ThemeName, type ThemeTokens } from "./themes";

type ThemeContextValue = {
  themeName: ThemeName;
  tokens: ThemeTokens;
  setTheme: (name: ThemeName) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themeName, setThemeName] = useState<ThemeName>("default");
  const tokens = themes[themeName];

  useEffect(() => {
    const root = document.documentElement;
    Object.entries(tokens).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    root.style.setProperty("font-family", tokens["--font-family"]);
  }, [tokens]);

  const value = useMemo(
    () => ({ themeName, tokens, setTheme: setThemeName }),
    [themeName, tokens],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
